import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { backendCall, createApiCall } from '@reducers/utils';
import { getAuthInfo } from '@reducers/user';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';

import type { AppDispatch, AppThunk } from '@reducers/utils/types';
import type { OnboardingWizardSubmitRequest } from '@api/domain/types';
import type { OnboardingWizardState } from './types';

const initialState: OnboardingWizardState = {
  submitting: false,
  error: null,
};

const onboardingWizardSlice = createSlice({
  name: 'onboardingWizard',
  initialState,
  reducers: {
    submitStarted: (state) => {
      state.submitting = true;
      state.error = null;
    },
    submitSucceeded: (state) => {
      state.submitting = false;
    },
    submitFailed: (state, action: PayloadAction<string>) => {
      state.submitting = false;
      state.error = action.payload;
    },
  },
});

const { submitStarted, submitSucceeded, submitFailed } = onboardingWizardSlice.actions;

export const selectOnboardingWizardSubmitting = (state: { onboardingWizard: OnboardingWizardState }) =>
  state.onboardingWizard.submitting;
export const selectOnboardingWizardError = (state: { onboardingWizard: OnboardingWizardState }) =>
  state.onboardingWizard.error;

export const submitOnboardingWizard =
  (payload: OnboardingWizardSubmitRequest): AppThunk<Promise<boolean>> =>
  async (dispatch: AppDispatch) => {
    dispatch(submitStarted());
    let succeeded = false;
    await dispatch(
      backendCall(
        createApiCall((api) => api.membership.submitOnboardingWizard, {
          params: payload,
          onSuccess: () => {
            dispatch(submitSucceeded());
            succeeded = true;
          },
          onError: (error) => {
            dispatch(submitFailed(error?.message ?? 'Failed to submit onboarding wizard'));
            dispatch(sendLog({ title: LOGGER_TITLES.ONBOARDING_WIZARD_SUBMIT_FAILED, data: { error: String(error) }, status: 'error' }));
          },
        })
      )
    );

    if (succeeded) {
      // Refresh profile in background so the next 403 lands on OnHold (not the wizard again).
      // Caller is responsible for navigating after the Thank you screen has been shown.
      dispatch(getAuthInfo()).catch(() => {});
    }
    return succeeded;
  };

export type { OnboardingWizardState } from './types';
export default onboardingWizardSlice.reducer;
